import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { listShortlist, removeFromShortlist } from "@/lib/api";
import { toast } from "sonner";
import { BookmarkX, ArrowUpRight, MapPin, LogIn } from "lucide-react";
import StatusTag from "@/components/StatusTag";

const fmtEUR = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Shortlist() {
  const { t } = useLang();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await listShortlist();
        setItems(data);
      } catch {
        // 401 here would be unexpected since we just confirmed auth; ignore quietly.
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  const onRemove = async (id) => {
    const prev = items;
    setItems((arr) => arr.filter((i) => i.id !== id));
    try {
      await removeFromShortlist(id);
      toast.success(t("shortlist.removedToast"));
    } catch {
      setItems(prev);
      toast.error(t("share.failed"));
    }
  };

  if (authLoading) {
    return <main className="max-w-5xl mx-auto px-6 md:px-10 py-16 text-sm font-mono text-[#6C706D]">…</main>;
  }

  if (!user) {
    return (
      <main data-testid="shortlist-login" className="max-w-md mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-display text-4xl text-[#282C2A]">{t("nav.shortlist")}</h2>
        <p className="mt-4 text-base text-[#6C706D]">{t("shortlist.loginRequired")}</p>
        <button
          data-testid="shortlist-signin"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            window.location.href =
              "https://auth.emergentagent.com/?redirect=" +
              encodeURIComponent(window.location.origin + "/");
          }}
          className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold tracking-widest uppercase bg-[#282C2A] text-white hover:bg-[#4A5D23] border border-[#282C2A] transition-colors"
        >
          <LogIn size={13} strokeWidth={1.5} />
          {t("nav.signIn")}
        </button>
      </main>
    );
  }

  return (
    <main data-testid="shortlist-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="label-eyebrow">{t("nav.shortlist")}</div>
      <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 text-[#282C2A]">
        {t("shortlist.title")}
      </h2>
      <p className="mt-3 text-base text-[#6C706D] max-w-2xl">{t("shortlist.sub")}</p>

      <div className="mt-10">
        {loading ? (
          <div className="text-sm font-mono text-[#6C706D]">…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="shortlist-empty"
            className="border-2 border-dashed border-[#E6E4DD] py-16 px-6 text-center"
          >
            <p className="text-base text-[#6C706D]">{t("shortlist.empty")}</p>
            <Link
              to="/"
              className="mt-6 inline-block text-xs font-semibold uppercase tracking-widest underline underline-offset-4"
            >
              ← {t("share.back").replace(/^← /, "")}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it) => (
              <article
                key={it.id}
                data-testid={`shortlist-card-${it.id}`}
                className="group relative flex flex-col bg-white border border-[#E6E4DD] hover:border-[#282C2A] transition-all overflow-hidden"
              >
                <a
                  href={it.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="relative h-44 overflow-hidden bg-[#F0EFEB] block"
                >
                  <img
                    src={it.image_url || "https://images.unsplash.com/photo-1632153630866-f45b76cc58e4?w=600&auto=format&fit=crop&q=70"}
                    alt={it.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-3 left-3">
                    <StatusTag status={it.status} />
                  </div>
                  <div className="absolute bottom-3 right-3 bg-white/95 border border-[#282C2A] px-2.5 py-1 font-serif-display text-2xl leading-none text-[#282C2A]">
                    {it.total_score}
                    <span className="text-xs text-[#6C706D] font-mono ml-0.5">/10</span>
                  </div>
                </a>
                <div className="flex-1 p-5 flex flex-col">
                  <div className="label-eyebrow inline-flex items-center gap-1.5">
                    <MapPin size={11} strokeWidth={1.5} />
                    {it.town || "—"}
                    {it.province && <span className="text-[#6C706D]"> · {it.province}</span>}
                  </div>
                  <h4 className="font-serif-display text-xl text-[#282C2A] leading-tight line-clamp-2 mt-2">
                    {it.title}
                  </h4>
                  <div className="mt-3 font-mono text-sm text-[#282C2A]">{fmtEUR(it.price)}</div>
                  <div className="mt-auto pt-5 border-t border-[#E6E4DD] flex items-center gap-3">
                    <button
                      type="button"
                      data-testid={`shortlist-remove-${it.id}`}
                      onClick={() => onRemove(it.id)}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider bg-white text-[#A0522D] border border-[#A0522D] hover:bg-[#A0522D] hover:text-white transition-colors"
                    >
                      <BookmarkX size={13} strokeWidth={1.5} />
                      {t("shortlist.remove")}
                    </button>
                    <a
                      href={it.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
                    >
                      {t("feed.viewListing")}
                      <ArrowUpRight size={13} strokeWidth={1.5} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
